import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

/**
 * Button Component
 *
 * A polished, accessible button component with multiple variants, sizes,
 * and states. Follows Stripe-level design standards.
 *
 * @example
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="secondary" leftIcon={<PlusIcon />}>Add Item</Button>
 * <Button variant="danger" loading>Deleting...</Button>
 */

const buttonVariants = {
  primary: `
    bg-wrg-blue-500 text-white
    hover:bg-wrg-blue-700
    focus-visible:ring-wrg-blue-500
    shadow-sm hover:shadow-md
  `,
  secondary: `
    bg-white text-gray-700
    border border-gray-200
    hover:bg-gray-50 hover:border-gray-300
    focus-visible:ring-gray-400
    shadow-sm
  `,
  ghost: `
    bg-transparent text-gray-600
    hover:bg-gray-100 hover:text-gray-900
    focus-visible:ring-gray-400
  `,
  danger: `
    bg-error-600 text-white
    hover:bg-error-700
    focus-visible:ring-error-500
    shadow-sm hover:shadow-md
  `,
  'danger-outline': `
    bg-white text-error-600
    border border-error-200
    hover:bg-error-50 hover:border-error-300
    focus-visible:ring-error-500
  `,
  success: `
    bg-success-700 text-white
    hover:bg-success-800
    focus-visible:ring-success-500
    shadow-sm hover:shadow-md
  `,
  'success-outline': `
    bg-white text-success-700
    border border-success-200
    hover:bg-success-50 hover:border-success-300
    focus-visible:ring-success-500
  `,
  link: `
    bg-transparent text-wrg-blue-500
    hover:text-wrg-blue-600 hover:underline
    focus-visible:ring-wrg-blue-500
    p-0
  `,
};

const buttonSizes = {
  xs: 'px-2.5 py-1 text-xs min-h-[28px]',
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-4 py-2 text-sm min-h-[38px]',
  lg: 'px-5 py-2.5 text-base min-h-[44px]',
  xl: 'px-6 py-3 text-base min-h-[50px]',
};

const iconOnlySizes = {
  xs: 'p-1 min-h-[28px] min-w-[28px]',
  sm: 'p-1.5 min-h-[32px] min-w-[32px]',
  md: 'p-2 min-h-[38px] min-w-[38px]',
  lg: 'p-2.5 min-h-[44px] min-w-[44px]',
  xl: 'p-3 min-h-[50px] min-w-[50px]',
};

const iconSizes = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-5 h-5',
  xl: 'w-6 h-6',
};

const spinnerSizes = {
  xs: 'w-3 h-3 border',
  sm: 'w-4 h-4 border',
  md: 'w-4 h-4 border-2',
  lg: 'w-5 h-5 border-2',
  xl: 'w-5 h-5 border-2',
};

const Spinner = ({ size = 'md', light = false }) => (
  <span
    className={clsx(
      'animate-spin rounded-full',
      spinnerSizes[size],
      light
        ? 'border-white/30 border-t-white'
        : 'border-gray-200 border-t-current'
    )}
    aria-hidden="true"
  />
);

const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      loading = false,
      disabled = false,
      fullWidth = false,
      iconOnly = false,
      rounded = false,
      className,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isLightSpinner = ['primary', 'danger', 'success'].includes(variant);

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={clsx(
          // Base styles
          'inline-flex items-center justify-center gap-2',
          'font-medium',
          'transition duration-200 ease-smooth',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          'active:scale-[0.98]',
          'select-none',
          'touch-target',

          // Variant styles
          buttonVariants[variant],

          // Size styles
          iconOnly ? iconOnlySizes[size] : buttonSizes[size],

          // Shape
          rounded ? 'rounded-full' : 'rounded-lg',

          // Width
          fullWidth && 'w-full',

          // Custom classes
          className
        )}
        {...props}
      >
        {/* Left icon or loading spinner */}
        {loading ? (
          <Spinner size={size} light={isLightSpinner} />
        ) : leftIcon ? (
          <span className={clsx(iconSizes[size], 'flex-shrink-0')}>
            {leftIcon}
          </span>
        ) : null}

        {!iconOnly && <span className="truncate">{children}</span>}

        {/* Right icon */}
        {rightIcon && !loading && (
          <span className={clsx(iconSizes[size], 'flex-shrink-0')}>
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

/**
 * Button Group Component
 *
 * Groups multiple buttons together with proper border handling
 */
const ButtonGroup = ({ children, className, vertical = false }) => {
  return (
    <div
      className={clsx(
        'inline-flex',
        vertical ? 'flex-col' : 'flex-row',
        // First child
        vertical
          ? '[&>*:first-child]:rounded-b-none'
          : '[&>*:first-child]:rounded-r-none',
        // Last child
        vertical
          ? '[&>*:last-child]:rounded-t-none'
          : '[&>*:last-child]:rounded-l-none',
        // Middle children
        '[&>*:not(:first-child):not(:last-child)]:rounded-none',
        // Border handling
        vertical
          ? '[&>*:not(:first-child)]:-mt-px'
          : '[&>*:not(:first-child)]:-ml-px',
        className
      )}
      role="group"
    >
      {children}
    </div>
  );
};

/**
 * Icon Button Component
 *
 * Convenient wrapper for icon-only buttons
 */
const IconButton = forwardRef(
  ({ icon, 'aria-label': ariaLabel, ...props }, ref) => {
    if (!ariaLabel) {
      console.warn('IconButton requires an aria-label for accessibility');
    }

    return (
      <Button ref={ref} iconOnly aria-label={ariaLabel} {...props}>
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

export { Button, ButtonGroup, IconButton };
export default Button;
